/* fvl-adv.js — replacement runtime for the listicle's interactive components.
 *
 * The page was ported from a GemPages export, and the clone step strips the
 * builder's own JavaScript as app code. That leaves <gp-countdown-timer>,
 * <gp-carousel> and the sticky bar as inert custom elements: they render, but
 * nothing counts down, slides or hides. This restores those three behaviours
 * with no dependencies.
 *
 * Everything is defensive — if a component is absent or its markup changes, the
 * relevant block exits quietly rather than throwing and killing the ones after it.
 */
(function () {
  'use strict';
  var ready = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  /* ---------------------------------------------------------------- countdown
   * Config on the element is behaviour:"evergreen", minutes+seconds enabled.
   * Evergreen means per-visitor rather than a fixed date, so the deadline is
   * persisted in localStorage: without that it resets on every refresh, which
   * is both obviously fake and worse than showing nothing. On expiry it holds
   * at 00:00 instead of looping.
   */
  function countdown() {
    var host = document.querySelector('gp-countdown-timer');
    if (!host) return;

    // the two-digit cells GemPages rendered; first is minutes, second seconds
    var cells = [];
    host.querySelectorAll('*').forEach(function (el) {
      if (el.children.length) return;
      if (/^\d{1,2}$/.test((el.textContent || '').trim())) cells.push(el);
    });
    if (cells.length < 2) return;
    var mEl = cells[0], sEl = cells[1];

    // 20 minutes, not 7. The article runs ~2,600 words plus imagery, so a short
    // window expires while the reader is still part-way down and leaves a dead
    // 00:00 sitting next to an offer that is still buyable.
    var MINUTES = 20, KEY = 'fvl_deal_deadline';
    var deadline = parseInt(localStorage.getItem(KEY) || '0', 10);
    var now = Date.now();
    // Persist across refreshes so it does not visibly jump back to full mid-read.
    // But roll a FRESH window whenever the stored one has already passed: only
    // resetting after 24h left anyone carrying an expired deadline — including
    // everyone who saw the earlier 7-minute version — with the block hidden for
    // good. Auto-hide should only ever fire for a timer that runs out while you
    // are on the page, never greet you on arrival.
    if (!deadline || deadline <= now) {
      deadline = now + MINUTES * 60000;
      try { localStorage.setItem(KEY, String(deadline)); } catch (e) {}
    }

    // On expiry hide the whole row rather than showing 00:00 — the builder's own
    // config exposed autoHideWhenTimeout for this and had it switched off.
    var row = host;
    while (row.parentElement && !/deal ending in/i.test(row.parentElement.textContent || '')) {
      row = row.parentElement;
    }
    if (row.parentElement) row = row.parentElement;

    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    (function tick() {
      var left = Math.max(0, deadline - Date.now());
      if (left <= 0) { row.style.display = 'none'; return; }
      var total = Math.floor(left / 1000);
      mEl.textContent = pad(Math.floor(total / 60));
      sEl.textContent = pad(total % 60);
      setTimeout(tick, 1000);
    })();
  }

  /* ----------------------------------------------------------------- carousel
   * Structure: gp-carousel > div > div[ prevBtn, .gem-slider, nextBtn ], with
   * five .gem-slider-item children and a dot container alongside. The items are
   * full-width inside an overflow-hidden parent, so the track is laid out as a
   * flex row here and moved by transform. Arrows, dots, touch swipe and keyboard
   * all drive the same setIndex.
   */
  function carousel() {
    document.querySelectorAll('gp-carousel').forEach(function (root) {
      var slider = root.querySelector('.gem-slider');
      if (!slider) return;
      var items = Array.prototype.slice.call(slider.children).filter(function (n) {
        return n.classList && n.classList.contains('gem-slider-item');
      });
      if (items.length < 2) return;

      // .gem-slider IS the overflow:hidden clipping box, so transforming it moves
      // the whole window across the page rather than moving slides inside it —
      // that is the "card slides away and leaves blank space" failure. Scroll the
      // container instead: correct by construction, and it restores real native
      // touch swipe with momentum rather than a hand-rolled drag.
      slider.style.display = 'flex';
      slider.style.overflowX = 'auto';
      slider.style.overflowY = 'hidden';
      slider.style.scrollBehavior = 'smooth';
      slider.style.scrollSnapType = 'x mandatory';
      slider.style.webkitOverflowScrolling = 'touch';
      slider.style.scrollbarWidth = 'none';          // Firefox
      slider.style.msOverflowStyle = 'none';         // old Edge
      items.forEach(function (it) { it.style.scrollSnapAlign = 'start'; it.style.flex = '0 0 auto'; });
      // WebKit scrollbar needs a stylesheet rule; inline cannot reach ::-webkit-
      if (!document.getElementById('fvl-carousel-css')) {
        var st = document.createElement('style');
        st.id = 'fvl-carousel-css';
        st.textContent = '.gem-slider::-webkit-scrollbar{display:none;height:0}';
        document.head.appendChild(st);
      }

      var i = 0;
      var dots = Array.prototype.slice.call(
        root.querySelectorAll('[class*="carousel-dot"] > *')
      );
      // Distances are MEASURED, never assumed.
      //   - a percentage is wrong: slides are 307px inside a 262px viewport, so
      //     translateX(-100%) undershoots by 45px per step and compounds.
      //   - offsetLeft is also wrong: it is relative to the offsetParent, which
      //     is not this slider, and reported a 307px pitch against a real 337px.
      // So measure each slide's true left against the slider with the transform
      // cleared, and translate absolutely by that.
      // Scroll distance is each slide's offset from the track's scroll origin.
      // Measured, not assumed: the slides are 307px wide but sit on a 337px
      // pitch, and offsetLeft reports against the wrong ancestor, so both a
      // percentage and offsetLeft give the wrong number here.
      var offsets = [];
      function measure() {
        var base = slider.getBoundingClientRect().left - slider.scrollLeft;
        offsets = items.map(function (it) {
          return Math.round(it.getBoundingClientRect().left - base);
        });
      }

      function setIndex(n) {
        i = (n + items.length) % items.length;
        slider.scrollTo({ left: offsets[i] || 0, behavior: 'smooth' });
        dots.forEach(function (d, k) { d.style.opacity = k === i ? '1' : '.4'; });
      }

      // keep the dots honest when the user swipes natively rather than clicking
      var st2;
      slider.addEventListener('scroll', function () {
        clearTimeout(st2);
        st2 = setTimeout(function () {
          var x = slider.scrollLeft, best = 0, d = Infinity;
          offsets.forEach(function (o, k) {
            if (Math.abs(o - x) < d) { d = Math.abs(o - x); best = k; }
          });
          i = best;
          dots.forEach(function (dt, k) { dt.style.opacity = k === i ? '1' : '.4'; });
        }, 120);
      }, { passive: true });

      var rt;
      window.addEventListener('resize', function () {
        clearTimeout(rt);
        rt = setTimeout(function () { measure(); setIndex(i); }, 150);
      });

      var btns = Array.prototype.slice.call(root.querySelectorAll('button'));
      if (btns[0]) btns[0].addEventListener('click', function (e) { e.preventDefault(); setIndex(i - 1); });
      if (btns[1]) btns[1].addEventListener('click', function (e) { e.preventDefault(); setIndex(i + 1); });
      dots.forEach(function (d, k) {
        d.style.cursor = 'pointer';
        d.addEventListener('click', function () { setIndex(k); });
      });

      // No hand-rolled touch handling: the container scrolls natively now, and a
      // manual drag would fight it. scroll-snap does the settling.

      measure();
      setIndex(0);
      // images finishing late can shift the slides, so re-measure once settled
      window.addEventListener('load', function () {
        setTimeout(function () { measure(); setIndex(i); }, 300);
      });
    });
  }

  /* ------------------------------------------------------------------- sticky
   * The sticky CTA overlaps the footer's own button at the end of the page, so
   * two competing calls to action sit on screen at once. Hide it once the final
   * CTA scrolls into view and bring it back on the way up.
   */
  function sticky() {
    var bar = document.querySelector('gp-sticky');
    if (!bar) {
      // the bar is sometimes a plain fixed-position wrapper rather than <gp-sticky>
      var cands = Array.prototype.slice.call(document.querySelectorAll('div,section'));
      for (var k = 0; k < cands.length; k++) {
        var cs = getComputedStyle(cands[k]);
        if ((cs.position === 'fixed' || cs.position === 'sticky') && cands[k].getBoundingClientRect().bottom > window.innerHeight - 200) { bar = cands[k]; break; }
      }
    }
    if (!bar) return;

    // Pick what to watch carefully. The bar sits INSIDE the final section, and
    // that section collapses to height 0 because a position:fixed child is out
    // of flow — so observing "the last section" watches a zero-height box that
    // contains the bar itself, and it can never fire. Take the last section that
    // has real height and does not contain the bar.
    var sections = Array.prototype.slice.call(
      document.querySelectorAll('[id^="shopify-section-fvl-adv-"]')
    ).filter(function (s) {
      return !s.contains(bar) && s.getBoundingClientRect().height > 40;
    });
    var last = sections[sections.length - 1];
    if (!last) return;

    // Do NOT touch transform. The bar centres itself with translateX(-50% of its
    // width) — computed as translateX(-195px) against left:195px — so assigning
    // any transform here drops that offset and throws the bar off to the right.
    // Opacity plus pointer-events hides it completely and cannot disturb layout.
    bar.style.transition = 'opacity .3s ease';
    var hide = function (on) {
      bar.style.opacity = on ? '0' : '1';
      bar.style.pointerEvents = on ? 'none' : '';
    };

    // Two independent gates decide visibility:
    //   revealed - the hero block (which carries its own CTA) has scrolled past.
    //              Before that the sticky bar just duplicates the button sitting
    //              directly above it.
    //   atFooter - the final section is in view, where it would cover the footer.
    var hero = document.getElementById('shopify-section-fvl-adv-03') || sections[0];
    var revealed = false, atFooter = false;
    var apply = function () { hide(!revealed || atFooter); };

    hide(true);                                   // start hidden, then fade in
    var onScroll = function () {
      var r = hero.getBoundingClientRect();
      var next = r.bottom <= 0;                   // hero fully above the viewport
      if (next !== revealed) { revealed = next; apply(); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { atFooter = en.isIntersecting; apply(); });
      // POSITIVE bottom margin extends the detection box below the viewport so
      // the bar clears just before the footer arrives. A negative value shrinks
      // it instead, which moved the trigger line above where the last section
      // ever reaches — it could not fire at all.
      }, { rootMargin: '0px 0px 140px 0px' }).observe(last);
    }
  }

  ready(function () { try { countdown(); } catch (e) {} try { carousel(); } catch (e) {} try { sticky(); } catch (e) {} });
})();
